import { ApplicationInsights, type ITelemetryItem } from '@microsoft/applicationinsights-web';

let appInsights: ApplicationInsights | null = null;

export const initializeAppInsights = (): ApplicationInsights | null => {
  if (appInsights) return appInsights;

  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;

  if (connectionString) {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        enableAjaxPerfTracking: true,
        enableResponseHeaderTracking: true,
        enableRequestHeaderTracking: true,

        enableUnhandledPromiseRejectionTracking: true,
        autoTrackPageVisitTime: true,

        enableCorsCorrelation: true,
        enablePerformance: true,

        disableFetchTracking: false,
        disableExceptionTracking: false,
        disableAjaxTracking: false,

        cookieDomain: document.domain,
        sessionRenewalMs: 30 * 60 * 1000
      }
    });

    appInsights.loadAppInsights();

    appInsights.addTelemetryInitializer((envelope: ITelemetryItem) => {
      // Shares one App Insights resource with the Onboarding Web App per env — these are the
      // standard AI dimensions for telling sources apart, filterable in every query.
      envelope.tags = envelope.tags || [];
      (envelope.tags as Record<string, string>)['ai.cloud.role'] = 'marketing-site';
      (envelope.tags as Record<string, string>)['ai.cloud.roleInstance'] = import.meta.env.VITE_ENV_NAME || 'local';

      // ITelemetryItem puts baseType/baseData at the top level; envelope.data is a *separate*
      // field for custom properties (always empty here) — envelope.data?.baseData/.baseType was
      // always undefined, so this whole block silently no-opped: the dependency exclusions below
      // never actually ran on any telemetry item, not just on ones that failed to match.
      const telemetryItem = envelope.baseData;
      if (telemetryItem) {
        telemetryItem.properties = telemetryItem.properties || {};
        telemetryItem.properties.environment = import.meta.env.MODE;
        telemetryItem.properties.appVersion = import.meta.env.VITE_VERSION || '1.0';
      }

      if (envelope.baseType === 'RemoteDependencyData') {
        const dependency = envelope.baseData || {};
        const url = dependency.target || dependency.name || '';

        const isGoogleApi = /www\.googleapis\.com/.test(url);
        const isIdentityToolkit = /identitytoolkit\.googleapis\.com/.test(url);
        const isCheckLogin = url.includes('check_login');
        const isClarity = /clarity\.ms/.test(url);
        const isGoogleAnalytics = /google-analytics\.com/.test(url);

        if (isGoogleApi || isIdentityToolkit || isCheckLogin || isClarity || isGoogleAnalytics) {
          return false;
        }
      }

      return true;
    });

    appInsights.trackPageView();
    if (import.meta.env.DEV) {
      appInsights.flush();
    }
  }

  return appInsights;
};

export const getAppInsightsInstance = (): ApplicationInsights | null => {
  if (!appInsights) {
    return initializeAppInsights();
  }
  return appInsights;
};
